import {FunctionRootsSolver} from './FunctionRootsSolver';

/**
 * Puppy CAD 内核 `math_FunctionAllRoots` 的容差零区间与离散根调度器。
 *
 * 职责：使用调用方初始均匀样本识别连续零带，再仅在零带之外调用 `FunctionRoots`，
 * 保证离散点不会同时落入重合 segment。
 */
export class FunctionAllRootsSolver {
    /** 离散根求解器。 */
    private readonly rootsSolver: FunctionRootsSolver;

    /**
     * 创建 Puppy CAD 内核 全根调度器。
     * @param rootsSolver 离散根求解器。
     */
    public constructor(rootsSolver: FunctionRootsSolver = new FunctionRootsSolver()) {
        this.rootsSolver = rootsSolver;
    }

    /**
     * 搜索离散根与容差零区间。
     *
     * @param func 一元函数。
     * @param firstParameter 参数域起点。
     * @param lastParameter 参数域终点。
     * @param sampleCount 初始均匀样本数量，包含首尾点。
     * @param epsilonX 参数容差。
     * @param epsilonF 离散根函数值容差。
     * @param epsilonNull 零区间函数值容差。
     * @returns Puppy CAD 内核 等价全根结果。
     */
    public solve(
        func: ScalarFunction,
        firstParameter: number,
        lastParameter: number,
        sampleCount: number,
        epsilonX: number,
        epsilonF: number,
        epsilonNull: number,
    ): FunctionAllRootsResult {
        const resolvedSampleCount = Math.max(2, Math.trunc(sampleCount));
        const parameters = Array.from({length: resolvedSampleCount}, (_unused, index) => (
            ((resolvedSampleCount - 1 - index) * firstParameter + index * lastParameter)
            / (resolvedSampleCount - 1)
        ));
        const values: number[] = [];
        for (const parameter of parameters) {
            const value = func.value(parameter);
            if (value === null || !Number.isFinite(value)) {
                return {done: false, roots: [], nullIntervals: []};
            }
            values.push(value);
        }

        const nullIntervals: FunctionNullInterval[] = [];
        let insideNullInterval = false;
        let currentStart = firstParameter;
        let startsAtDomainBoundary = false;
        for (let index = 1; index < parameters.length; index += 1) {
            const previousIsNull = Math.abs(values[index - 1]!) <= epsilonNull;
            const currentIsNull = Math.abs(values[index]!) <= epsilonNull;
            if (!insideNullInterval && previousIsNull && currentIsNull) {
                insideNullInterval = true;
                startsAtDomainBoundary = index === 1;
                if (startsAtDomainBoundary) {
                    currentStart = parameters[0]!;
                } else {
                    const boundary = this.resolveNullBoundary(
                        func,
                        parameters[index - 2]!,
                        parameters[index - 1]!,
                        epsilonX,
                        epsilonF,
                        epsilonNull,
                        true,
                    );
                    if (boundary === null) return {done: false, roots: [], nullIntervals: []};
                    currentStart = boundary;
                }
            }
            if (insideNullInterval && !currentIsNull) {
                const boundary = this.resolveNullBoundary(
                    func,
                    parameters[index - 1]!,
                    parameters[index]!,
                    epsilonX,
                    epsilonF,
                    epsilonNull,
                    false,
                );
                if (boundary === null) return {done: false, roots: [], nullIntervals: []};
                nullIntervals.push({
                    startParameter: currentStart,
                    endParameter: boundary,
                    startsAtDomainBoundary,
                    endsAtDomainBoundary: false,
                });
                insideNullInterval = false;
            }
        }
        if (insideNullInterval) {
            nullIntervals.push({
                startParameter: currentStart,
                endParameter: lastParameter,
                startsAtDomainBoundary,
                endsAtDomainBoundary: true,
            });
        }

        if (nullIntervals.length === 0) {
            const roots = this.rootsSolver.solve(
                func,
                firstParameter,
                lastParameter,
                resolvedSampleCount,
                epsilonX,
                epsilonF,
            );
            return {done: roots.done, roots: roots.roots, nullIntervals: []};
        }

        const roots: number[] = [];
        const gaps = [
            {start: firstParameter, end: nullIntervals[0]!.startParameter},
            ...nullIntervals.slice(0, -1).map((interval, index) => ({
                start: interval.endParameter,
                end: nullIntervals[index + 1]!.startParameter,
            })),
            {start: nullIntervals.at(-1)!.endParameter, end: lastParameter},
        ];
        const fullLength = lastParameter - firstParameter;
        for (const gap of gaps) {
            if (gap.end - gap.start <= epsilonX) continue;
            const gapSampleCount = Math.max(
                3,
                Math.trunc((gap.end - gap.start) / fullLength * resolvedSampleCount),
            );
            const result = this.rootsSolver.solve(
                func,
                gap.start,
                gap.end,
                gapSampleCount,
                epsilonX,
                epsilonF,
            );
            if (!result.done) return {done: false, roots: [], nullIntervals: []};
            result.roots.forEach((root) => {
                if (!roots.some((candidate) => Math.abs(candidate - root) <= epsilonX)) roots.push(root);
            });
        }
        roots.sort((left, right) => left - right);
        return {done: true, roots, nullIntervals};
    }

    /**
     * 在相邻外部样本与零带样本之间求 `F=±EpsNul` 边界。
     * @param func 一元函数。
     * @param first 区间起点。
     * @param last 区间终点。
     * @param epsilonX 参数容差。
     * @param epsilonF 函数值容差。
     * @param epsilonNull 零带容差。
     * @param selectMaximum 起点边界取较大根，终点边界取较小根。
     * @returns 边界参数；两侧均无法求根时返回 `null`。
     */
    private resolveNullBoundary(
        func: ScalarFunction,
        first: number,
        last: number,
        epsilonX: number,
        epsilonF: number,
        epsilonNull: number,
        selectMaximum: boolean,
    ): number | null {
        const candidates = [epsilonNull, -epsilonNull].flatMap((target) => {
            const result = this.rootsSolver.solve(func, first, last, 3, epsilonX, epsilonF, target);
            return result.done ? result.roots : [];
        });
        if (candidates.length === 0) return null;
        return selectMaximum ? Math.max(...candidates) : Math.min(...candidates);
    }
}
